import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate, NavLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useCart } from '../contexts/CartContext';
import { tenantUrl } from '../utils/tenantHost';
import { getTenantLogo } from '../utils/tenantBranding';
import NotificationBell from './NotificationBell';

const ROLE_NAV = {
  ADMIN: {
    label: 'Admin', color: '#4f46e5', bgLight: '#eef2ff',
    initials: (n) => n?.slice(0, 2).toUpperCase() || 'AD',
    links: [
      { to: '/dashboard/super-admin', label: 'Dashboard'       },
      { to: '/inventory',             label: 'Inventory'       },
      { to: '/tenants',               label: 'Tenants'         },
      { to: '/admin/doctors',         label: 'Manage Doctors'  },
      { to: '/admin/patients',        label: 'Manage Patients' },
    ],
  },
  super_admin: {
    label: 'Super Admin', color: '#4f46e5', bgLight: '#eef2ff',
    initials: (n) => n?.slice(0, 2).toUpperCase() || 'SA',
    links: [
      { to: '/dashboard/super-admin', label: 'Dashboard' },
    ],
  },
  system_admin: {
    label: 'System Admin', color: '#2563eb', bgLight: '#eff6ff',
    initials: (n) => n?.slice(0, 2).toUpperCase() || 'SY',
    links: [
      { to: '/dashboard/system-admin', label: 'Dashboard' },
    ],
  },
  tenant_admin: {
    label: 'Tenant Admin', color: '#0891b2', bgLight: '#ecfeff',
    initials: (n) => n?.slice(0, 2).toUpperCase() || 'TA',
    links: [
      { to: '/dashboard/tenant-admin', label: 'Dashboard' },
      { to: '/services/doctors',       label: 'Find Doctors' },
      { to: '/inventory',              label: 'Inventory' },
    ],
  },
  staff: {
    label: 'Staff', color: '#059669', bgLight: '#ecfdf5',
    initials: (n) => n?.slice(0, 2).toUpperCase() || 'ST',
    links: [
      { to: '/dashboard/staff', label: 'Dashboard'    },
      { to: '/appointments',    label: 'Appointments' },
      { to: '/patients',        label: 'Patients'     },
    ],
  },
  doctor: {
    label: 'Doctor', color: '#7c3aed', bgLight: '#f5f3ff',
    initials: (n) => n?.slice(0, 2).toUpperCase() || 'DR',
    links: [
      { to: '/dashboard/doctor', label: 'Dashboard'    },
      { to: '/appointments',     label: 'Appointments' },
      { to: '/doctor/schedule',  label: 'My Schedule'  },
    ],
  },
  DOCTOR: {
    label: 'Doctor', color: '#7c3aed', bgLight: '#f5f3ff',
    initials: (n) => n?.slice(0, 2).toUpperCase() || 'DR',
    links: [
      { to: '/dashboard/doctor', label: 'Dashboard'    },
      { to: '/appointments',     label: 'Appointments' },
      { to: '/doctor/schedule',  label: 'My Schedule'  },
    ],
  },
  PATIENT: {
    label: 'Patient', color: '#d97706', bgLight: '#fffbeb',
    initials: (n) => n?.slice(0, 2).toUpperCase() || 'PT',
    links: [
      { to: '/dashboard/patient', label: 'Dashboard'    },
      { to: '/services/doctors',  label: 'Find Doctors' },
      { to: '/appointments',      label: 'Appointments' },
      { to: '/orders',            label: 'My Orders'    },
    ],
  },
  end_user: {
    label: 'Patient', color: '#d97706', bgLight: '#fffbeb',
    initials: (n) => n?.slice(0, 2).toUpperCase() || 'PT',
    links: [
      { to: '/dashboard/patient', label: 'Dashboard'    },
      { to: '/services/doctors',  label: 'Find Doctors' },
      { to: '/orders',            label: 'My Orders'    },
    ],
  },
};
const TENANT_PICKER_ROLES = new Set(['ADMIN']);
// Platform-level roles aren't scoped to a single tenant, so the logo keeps the ArogyaOne brand.
const PLATFORM_ROLES = new Set(['ADMIN', 'super_admin', 'system_admin']);

/* ── Icons ────────────────────────────────────────────────────────── */
const CartIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
    <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
  </svg>
);
const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
    <circle cx="11" cy="11" r="7.5"/><line x1="20.5" y1="20.5" x2="16.3" y2="16.3"/>
  </svg>
);
const IconChevron = ({ down }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
    strokeLinecap="round" strokeLinejoin="round" width="14" height="14"
    style={{ transition: 'transform .2s', transform: down ? 'rotate(0deg)' : 'rotate(-90deg)' }}>
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);
const IconMenu = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
    strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
    <line x1="3" y1="12" x2="21" y2="12"/>
    <line x1="3" y1="6"  x2="21" y2="6"/>
    <line x1="3" y1="18" x2="21" y2="18"/>
  </svg>
);
const IconClose = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
    strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6"  y1="6" x2="18" y2="18"/>
  </svg>
);
const IconLogout = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <polyline points="16 17 21 12 16 7"/>
    <line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);
const IconBuilding = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
    <rect x="3" y="3" width="18" height="18" rx="2"/>
    <path d="M9 3v18M15 3v18M3 9h18M3 15h18"/>
  </svg>
);
const IconCheck = ({ color }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5"
    strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const IconProfile = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
  </svg>
);
const IconSettings = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <circle cx="12" cy="12" r="3"/>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
  </svg>
);

/* ── TenantPicker ─────────────────────────────────────────────────── */
// Each tenant lives on its own subdomain (and its own login session), so
// "switching" opens that tenant's site rather than changing in-app state.
const TenantPicker = ({ accentColor, bgLight }) => {
  const { tenant, tenants: allTenants } = useTenant();
  const tenants = allTenants.filter(t => t.subdomain);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const borderColor = tenant ? accentColor + '44' : '#f59e0b44';

  const handleSelect = (t) => {
    setOpen(false);
    if (t.id !== tenant?.id) window.location.assign(tenantUrl(t.subdomain));
  };

  return (
    <div ref={ref} className="rel">
      <button className="tenant-trigger"
        onClick={() => setOpen(v => !v)}
        style={{ background: tenant ? bgLight : '#fef3c7', border: `1.5px solid ${borderColor}` }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = accentColor; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = borderColor; }}>
        <IconBuilding />
        <span className="tenant-name" style={{ color: tenant ? accentColor : '#92400e' }}>
          {tenant ? tenant.name : 'Select Tenant'}
        </span>
        <IconChevron down={open} />
      </button>

      {open && (
        <div className="dropdown dropdown-left">
          <div className="dropdown-header">Switch Tenant</div>
          <div className="tenant-scroll">
            {tenants.length === 0 && (
              <div className="no-tenants-msg">No tenants available</div>
            )}
            {tenants.map(t => {
              const isActive = tenant?.id === t.id;
              return (
                <button key={t.id} className="tenant-item"
                  onClick={() => handleSelect(t)}
                  style={{ background: isActive ? bgLight : 'none' }}>
                  <div className="tenant-item-avatar"
                    style={{
                      background: isActive
                        ? `linear-gradient(135deg, ${accentColor}dd, ${accentColor}88)`
                        : 'linear-gradient(135deg, #eef2ff, #c7d2fe)',
                      color: isActive ? '#fff' : '#6366f1',
                    }}>
                    {t.name.slice(0, 2).toUpperCase()}
                  </div>
                  <div className="tenant-item-text-wrap">
                    <div className="tenant-item-name"
                      style={{ fontWeight: isActive ? 600 : 500, color: isActive ? accentColor : '#1f2937' }}>
                      {t.name}
                    </div>
                    <div className="tenant-item-slug">{t.subdomain}</div>
                  </div>
                  {isActive && (
                    <span className="tenant-item-check"><IconCheck color={accentColor} /></span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Navbar ───────────────────────────────────────────────────────── */
const Navbar = () => {
  const { user, logout } = useAuth();
  const { totalItems }   = useCart();
  const { tenant }       = useTenant();
  const navigate         = useNavigate();
  const location         = useLocation();

  const [menuOpen, setMenuOpen] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const dropRef = useRef(null);

  const role        = ROLE_NAV[user?.role] || ROLE_NAV['end_user'];
  // On a clinic's subdomain the header carries the clinic's brand for visitors,
  // patients and doctors alike; only platform-level roles keep ArogyaOne.
  const showTenantBrand = !!tenant && !(user && PLATFORM_ROLES.has(user.role));
  const tenantLogo      = getTenantLogo(tenant?.subdomain);
  const accentColor = role.color;
  const bgLight     = role.bgLight;

  useEffect(() => {
    const h = (e) => dropRef.current && !dropRef.current.contains(e.target) && setDropOpen(false);
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const handleLogout = () => { logout(); navigate('/login'); };

  // Close dropdown then navigate
  const handleDropNav = (path) => { setDropOpen(false); navigate(path); };

  // Header search feeds the existing doctor search (DoctorList reads `q`).
  const [headerQuery, setHeaderQuery] = useState('');
  const showHeaderSearch = !TENANT_PICKER_ROLES.has(user?.role);
  const handleHeaderSearch = (e) => {
    e.preventDefault();
    const q = headerQuery.trim();
    navigate(q ? `/services/doctors?q=${encodeURIComponent(q)}` : '/services/doctors');
    setHeaderQuery('');
  };

  return (
    <nav className="navbar">
      <div className="navbar-inner">

        <button className="navbar-logo" onClick={() => navigate('/')}>
          {showTenantBrand && tenantLogo ? (
            <img className="navbar-logo-img" src={tenantLogo} alt={tenant.name} />
          ) : (<>
          <div className="navbar-logo-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"
              strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
            </svg>
          </div>
          <span className="navbar-logo-text">
            {showTenantBrand ? tenant.name : 'ArogyaOne'}
          </span>
          </>)}
        </button>

        <div className="navbar-links">
          {role.links.map(link => (
            <NavLink key={link.to} to={link.to} className="nav-link">
              {link.label}
            </NavLink>
          ))}
        </div>

        <div className="navbar-right">
          {showHeaderSearch && (
            <form className="navbar-search" onSubmit={handleHeaderSearch} role="search">
              <SearchIcon />
              <input
                type="text"
                value={headerQuery}
                onChange={e => setHeaderQuery(e.target.value)}
                placeholder="Search doctors, specialties…"
                aria-label="Search doctors"
              />
            </form>
          )}

          {TENANT_PICKER_ROLES.has(user?.role) && (
            <div className="tenant-picker-wrap">
              <TenantPicker accentColor={accentColor} bgLight={bgLight} />
            </div>
          )}

          <button className="btn-icon" onClick={() => navigate('/cart')} aria-label="Cart">
            <CartIcon />
            {totalItems > 0 && (
              <span className="cart-badge">{totalItems > 99 ? '99+' : totalItems}</span>
            )}
          </button>

          {user && <NotificationBell />}

          {user ? (
            <div ref={dropRef} className="rel">
              <button className="navbar-user-btn" onClick={() => setDropOpen(v => !v)}>
                <div className="avatar avatar-sm"
                  style={{ background: `linear-gradient(135deg, ${accentColor}, ${accentColor}bb)` }}>
                  {role.initials(user?.name)}
                </div>
                <span className="navbar-user-meta">
                  <span className="navbar-user-role">{role.label}</span>
                  <span className="navbar-username">{user?.name || user?.phone}</span>
                </span>
                <IconChevron down={dropOpen} />
              </button>

              {dropOpen && (
                <div className="dropdown dropdown-right">
                  <div className="dropdown-user-info">
                    <div className="drop-user-name">{user?.name || user?.phone}</div>
                    <div className="drop-user-email">{user?.email || user?.phone}</div>
                    <div className="badge" style={{ marginTop: '7px', background: bgLight, border: `1px solid ${accentColor}33`, color: accentColor }}>
                      {role.label}
                    </div>
                  </div>
                  <div className="dropdown-items">
                    <button className="dropdown-item" onClick={() => handleDropNav('/profile')}>
                      <IconProfile /> Profile
                    </button>
                    <button className="dropdown-item" onClick={() => handleDropNav('/settings')}>
                      <IconSettings /> Settings
                    </button>
                    <div className="divider" />
                    <button className="dropdown-item dropdown-item-logout" onClick={handleLogout}>
                      <IconLogout /> Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <button className="btn-signin" onClick={() => navigate('/login')}>Sign in</button>
          )}

          <button className="mobile-hamburger" onClick={() => setMenuOpen(v => !v)} aria-label="Toggle navigation">
            {menuOpen ? <IconClose /> : <IconMenu />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="mobile-menu">
          {TENANT_PICKER_ROLES.has(user?.role) && (
            <div className="mobile-menu-tenant">
              <TenantPicker accentColor={accentColor} bgLight={bgLight} />
            </div>
          )}
          {role.links.map(link => (
            <NavLink key={link.to} to={link.to} className="nav-link nav-link-mobile">
              {link.label}
            </NavLink>
          ))}
        </div>
      )}
    </nav>
  );
};

export default Navbar;